import { ASO_PRICING_FIELDS, ASO_TEMPLATES, getDefaultAsoPricing, sidebarOrder } from "./asoModel.js";
import StopLossMarkdownField from "./StopLossMarkdownField.jsx";

const PRICING_FIELDS = new Map(ASO_PRICING_FIELDS.map((field) => [field.key, field]));

const AsoForm = ({
    draft,
    onPatch,
    scrollPreviewTo,
    blurPreviewField,
    parsingPdf,
    dropKind,
    fileError,
    onStopLossPdf,
    onWorkbook,
}) => {
    const templateType = ASO_TEMPLATES[draft.templateType] ? draft.templateType : "aso";
    const pricing = { ...getDefaultAsoPricing(templateType), ...(draft.asoPricing || {}) };
    const dateValue = /^\d{4}-\d{2}-\d{2}$/.test(draft.proposalDate || "") ? draft.proposalDate : "";

    function setPricing(key, value) {
        onPatch({ asoPricing: { ...pricing, [key]: value } }, { [key]: value });
    }

    function focus(section, field) {
        return () => scrollPreviewTo(section, field);
    }

    function renderItem(key) {
        const pricingField = PRICING_FIELDS.get(key);
        if (pricingField) {
            return (
                <label key={key}>
                    {pricingField.label}
                    <textarea rows={2} value={pricing[key] || ""} onFocus={focus("pricing", key)} onBlur={() => blurPreviewField(key)} onChange={(event) => setPricing(key, event.target.value)} />
                </label>
            );
        }
        switch (key) {
            case "companyName":
                return (
                    <label key={key}>
                        Company
                        <input value={draft.companyName || ""} onFocus={focus("cover", "companyName")} onBlur={() => blurPreviewField("companyName")} onChange={(event) => onPatch({ companyName: event.target.value })} />
                    </label>
                );
            case "proposalDate":
                return (
                    <label key={key}>
                        Proposal date
                        <input type="date" value={dateValue} onFocus={focus("cover", "proposalDate")} onBlur={() => blurPreviewField("proposalDate")} onChange={(event) => onPatch({ proposalDate: event.target.value })} />
                    </label>
                );
            case "openingSentence":
                return (
                    <label key={key}>
                        Opening sentence
                        <textarea rows={2} value={draft.asoOpeningSentence || ""} placeholder="Optional sentence before the solution paragraph" onFocus={focus("planOffering", "openingSentence")} onBlur={() => blurPreviewField("openingSentence")} onChange={(event) => onPatch({ asoOpeningSentence: event.target.value }, { openingSentence: event.target.value })} />
                    </label>
                );
            case "planOffering":
                return (
                    <label key={key}>
                        Plan offering
                        <select value={draft.planOfferingVariant || "partnership-cigna"} onFocus={focus("planOffering", "planOffering")} onBlur={() => blurPreviewField("planOffering")} onChange={(event) => onPatch({ planOfferingVariant: event.target.value })}>
                            <option value="partnership-cigna">Partnership and Cigna</option>
                            <option value="partnership-only">Partnership only</option>
                        </select>
                    </label>
                );
            case "employeeTerm":
                return (
                    <label key={key}>
                        People
                        <input value={draft.asoWiEmployeeTerm || "employees"} onFocus={focus("cover", "employeeTerm")} onBlur={() => blurPreviewField("employeeTerm")} onChange={(event) => onPatch({ asoWiEmployeeTerm: event.target.value }, { employeeTerm: event.target.value })} />
                    </label>
                );
            case "solution":
                return (
                    <label key={key}>
                        Solution
                        <select value={draft.asoWiSolutionVariant || "wi-both"} onFocus={focus("planOffering", "solution")} onBlur={() => blurPreviewField("solution")} onChange={(event) => onPatch({ asoWiSolutionVariant: event.target.value })}>
                            <option value="wi-both">Slice and full replacement</option>
                            <option value="wi-slice">Slice</option>
                            <option value="wi-full-replacement">Full replacement</option>
                        </select>
                    </label>
                );
            case "cignaFootprint":
                return (
                    <label key={key} className="commercial-proposal__check">
                        <input type="checkbox" checked={draft.includeAsoWiCigna !== false} onFocus={focus("planOffering", "cignaFootprint")} onBlur={() => blurPreviewField("cignaFootprint")} onChange={(event) => onPatch({ includeAsoWiCigna: event.target.checked })} />
                        Cigna footprint
                    </label>
                );
            case "workbook":
                return (
                    <div key={key}>
                        <label className={`commercial-proposal__drop${dropKind === "workbook" ? " is-over" : ""}`}>
                            Workbook
                            <input type="file" accept=".xlsx,.xlsm,.xls" onChange={onWorkbook} />
                            <span>or drop an .xlsx or .xlsm on this page. Fills the plan design table.</span>
                        </label>
                        {draft.sourceFileName ? <p className="commercial-proposal__file">{draft.sourceFileName}</p> : null}
                        {fileError ? <p className="commercial-proposal__error">{fileError}</p> : null}
                        {draft.mapNotes?.length ? (
                            <ul className="commercial-proposal__notes">
                                {draft.mapNotes.map((note) => <li key={note}>{note}</li>)}
                            </ul>
                        ) : null}
                    </div>
                );
            case "networkAccessPage":
                return (
                    <label key={key} className="commercial-proposal__check">
                        <input type="checkbox" checked={draft.includeAsoWiNetworkAccess !== false} onFocus={focus("networkAccess", "networkAccess")} onBlur={() => blurPreviewField("networkAccess")} onChange={(event) => onPatch({ includeAsoWiNetworkAccess: event.target.checked })} />
                        Network access page
                    </label>
                );
            case "geoAccess":
                return (
                    <label key={key} className="commercial-proposal__check">
                        <input type="checkbox" checked={draft.includeAsoWiGeoAccess !== false} onFocus={focus("networkAccess", "geoAccess")} onBlur={() => blurPreviewField("geoAccess")} onChange={(event) => onPatch({ includeAsoWiGeoAccess: event.target.checked })} />
                        Geo-access analysis
                    </label>
                );
            case "pricingHeading":
                return <div key={key} className="commercial-proposal__group">Pricing</div>;
            case "proprietaryNetwork":
                return (
                    <label key={key}>
                        Proprietary network
                        <input value={draft.asoProprietaryNetwork || ""} onFocus={focus("pricing", "proprietaryNetwork")} onBlur={() => blurPreviewField("proprietaryNetwork")} onChange={(event) => onPatch({ asoProprietaryNetwork: event.target.value }, { proprietaryNetwork: event.target.value })} />
                    </label>
                );
            case "pricingHint":
                return <p key={key} className="commercial-proposal__hint">Clear a fee to take that row off the page.</p>;
            case "illustrative":
                return (
                    <label key={key} className="commercial-proposal__check">
                        <input type="checkbox" checked={Boolean(draft.includeIllustrativeAsoPremium)} onFocus={focus("illustrativeAsoPremium", "illustrativeAsoPremium")} onBlur={() => blurPreviewField("illustrativeAsoPremium")} onChange={(event) => onPatch({ includeIllustrativeAsoPremium: event.target.checked })} />
                        Illustrative ASO premium
                    </label>
                );
            case "equivalents":
                return (
                    <label key={key} className="commercial-proposal__check">
                        <input type="checkbox" checked={Boolean(draft.includeAsoEquivalents)} onFocus={focus("equivalents", "equivalents")} onBlur={() => blurPreviewField("equivalents")} onChange={(event) => onPatch({ includeAsoEquivalents: event.target.checked })} />
                        Equivalents
                    </label>
                );
            case "proposedPbmName":
                return (
                    <label key={key}>
                        Proposed PBM
                        <input value={draft.asoProposedPbmName || ""} placeholder="Shows the pharmacy appendix when filled" onFocus={focus("pharmacy", "proposedPbmName")} onBlur={() => blurPreviewField("proposedPbmName")} onChange={(event) => onPatch({ asoProposedPbmName: event.target.value })} />
                    </label>
                );
            case "cignaPbm":
                return (
                    <label key={key} className="commercial-proposal__check">
                        <input type="checkbox" checked={Boolean(draft.includeAsoWiCignaPbm)} onFocus={focus("pharmacy", "cignaPbm")} onBlur={() => blurPreviewField("cignaPbm")} onChange={(event) => onPatch({ includeAsoWiCignaPbm: event.target.checked })} />
                        Cigna PBM
                    </label>
                );
            case "stopLossQuotes":
                return (
                    <label key={key} className="commercial-proposal__check">
                        <input type="checkbox" checked={Boolean(draft.includeAsoStopLossQuotes)} onFocus={focus("appendixStopLoss", "stopLossQuotes")} onBlur={() => blurPreviewField("stopLossQuotes")} onChange={(event) => onPatch({ includeAsoStopLossQuotes: event.target.checked })} />
                        Stop-loss quotes included
                    </label>
                );
            case "stopLoss":
                return (
                    <div key={key}>
                        <label className={`commercial-proposal__drop${dropKind === "pdf" ? " is-over" : ""}`}>
                            Stop-loss contingencies
                            <input type="file" accept=".pdf,application/pdf" disabled={parsingPdf} onChange={onStopLossPdf} />
                            <span>{parsingPdf ? "Reading the PDF…" : "or drop a stop-loss PDF on this page"}</span>
                        </label>
                        {draft.stopLossFileName ? <p className="commercial-proposal__file">{draft.stopLossFileName}</p> : null}
                        {draft.stopLossNote ? <p className="commercial-proposal__hint">{draft.stopLossNote}</p> : null}
                        <StopLossMarkdownField
                            value={draft.stopLossContingencies || ""}
                            placeholder="Markdown. Headings, blank lines, and - bullets. They replace the Stop-loss section."
                            onFocus={focus("appendixStopLoss", "stopLossContingencies")}
                            onBlur={() => blurPreviewField("stopLossContingencies")}
                            onChange={(value) => onPatch({ stopLossContingencies: value, stopLossNote: "" })}
                        />
                    </div>
                );
            default:
                return null;
        }
    }

    return <>{sidebarOrder(templateType).map(renderItem)}</>;
};

export default AsoForm;
